import { useContext, useEffect, useState as classicUseState } from 'react';

import VectorIcon from '@assets/svg/vector.svg';

import Rolling from '@assets/gif/rolling.gif';
import { IFieldConfiguration } from '@models/FieldConfiguration.model';
import { IEquipmentInventory } from '@models/EquipmentInventory.model';
import { AssetContext } from '@pages/Asset/Asset.query';
import { indexFieldConfiguration } from '@services/queries/fieldsConfiguration';
import { useAppDispatch, useAppSelector } from '@store/redux/store';
import { handleSortClick } from '@utils';
import { useInView } from 'react-intersection-observer';
import { useQuery } from 'react-query';
import { ItemComponent } from './ListEquipmentItemComponent';
import { ListEquipmentsTagError } from './ListEquipmentsTag.error';
import { ListEquipmentsTagLoading } from './ListEquipmentsTag.loading';
import {
  Container,
  Icon,
  LoadingImg,
  LoadingText,
  LoadMore,
  RowDiv,
  Table,
  TbodyTag,
  Th,
  Thead,
  Trh,
} from './ListEquipmentsTag.styles';

interface ListEquipmentsTagProps {
  data?: IEquipmentInventory[];
  isLoading: boolean;
  isError: boolean;
  loadMore: () => void;
  startSearch: (sort?: string, direction?: 'ASC' | 'DESC' | undefined) => void;
  hasMore?: boolean;
  onClose: () => void;
}

const ListEquipmentsTag = ({
  data,
  isLoading,
  isError,
  loadMore,
  hasMore,
  startSearch,
  onClose,
}: ListEquipmentsTagProps) => {
  const { equipmentQuery } = useContext(AssetContext);

  const equipment = equipmentQuery?.data;

  const { data: config } = useQuery(
    ['fieldsConfiguration', 'productTagTable'],
    () => indexFieldConfiguration('productTagTable'),
  );
  const { ref, inView } = useInView();
  const dispatch = useAppDispatch();
  const { filter } = useAppSelector();
  const [configuration, updateConfiguration] = classicUseState<
    IFieldConfiguration[]
  >([]);
  if (config && configuration.length === 0) {
    updateConfiguration(config);
  }
  useEffect(() => {
    if (inView && hasMore) loadMore();
  }, [inView, hasMore]);
  if (isLoading) {
    return <ListEquipmentsTagLoading />;
  }
  if (isError || !data || !config) {
    return <ListEquipmentsTagError />;
  }

  if (configuration.length > 6) configuration.slice(0, 5);
  const widthResult = 66 / configuration.length;

  interface ArrowProps {
    headerSort: string;
  }
  const ArrowSort = ({ headerSort }: ArrowProps) => {
    return (
      <>
        {filter.options?.sort === headerSort ? (
          <div>
            <Icon
              isDesc={filter.options?.direction === 'DESC'}
              src={VectorIcon}
              alt=""
            />
          </div>
        ) : (
          <></>
        )}
      </>
    );
  };
  interface THeaderProps {
    filterString: string;
    label: string;
    width: number;
    isCustomField: boolean;
  }

  const FieldHeader = ({
    filterString,
    label,
    width,
    isCustomField,
  }: THeaderProps) => (
    <Th
      widthPercentage={width}
      isActive={filter.options?.sort === filterString}
    >
      <RowDiv
        onClick={() => {
          if (!isCustomField)
            handleSortClick(
              '',
              filterString,
              filter.options,
              dispatch,
              (_, sort, dir) => startSearch(sort, dir),
            );
        }}
        isCustomField={isCustomField}
      >
        {label}
        <ArrowSort headerSort={filterString} />
      </RowDiv>
    </Th>
  );

  if (!equipment) {
    return <div />;
  }
  return (
    <Container>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Table>
          <Thead>
            <Trh>
              {configuration.map((headerField, index) => {
                const isCustomField = headerField.customField !== undefined;
                if (headerField.fieldName === 'productName')
                  return (
                    <FieldHeader
                      label={headerField.name}
                      filterString={headerField.fieldFilter}
                      width={widthResult + 6}
                      isCustomField={isCustomField}
                    />
                  );
                if (index === 0)
                  return (
                    <FieldHeader
                      label={headerField.name}
                      filterString={headerField.fieldFilter}
                      width={widthResult + 2}
                      isCustomField={isCustomField}
                    />
                  );
                return (
                  <FieldHeader
                    label={headerField.name}
                    filterString={headerField.fieldFilter}
                    width={widthResult}
                    isCustomField={isCustomField}
                  />
                );
              })}

              <FieldHeader
                label="Tags"
                width={28}
                filterString="tags.name"
                isCustomField={false}
              />
            </Trh>
          </Thead>

          <TbodyTag>
            <ItemComponent
              item={equipment}
              first
              configuration={configuration}
              widthResult={widthResult}
              onClose={onClose}
            />

            {data
              .filter(item => item.id !== equipment.id)
              .map(item => {
                return (
                  <ItemComponent
                    itemArrayPaginated={item}
                    configuration={configuration}
                    widthResult={widthResult}
                    onClose={onClose}
                  />
                );
              })}
            <LoadMore ref={ref}>
              {hasMore ? (
                <LoadMore>
                  <LoadingText>Loading more</LoadingText>
                  <LoadingImg src={Rolling} alt="loading" />
                </LoadMore>
              ) : (
                <div />
              )}
            </LoadMore>
          </TbodyTag>
        </Table>
      </div>
    </Container>
  );
};

export { ListEquipmentsTag };
